import React, { useState, useContext, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useQuery, useMutation } from 'react-query'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import { jsPDF as JsPDF } from 'jspdf'
import useKeyboardShortcut from 'use-keyboard-shortcut'

import Sidebar from './Sidebar'
import type { DesignBoardItem } from '../types'
import DesignBoard from './DesignBoard'
import { v4 as uuidv4 } from 'uuid'
import { actions, DesignContext, EMPTY_STATE, getStorageKey, SAVED_DESIGN_ID_KEY, UNSAVED_DESIGN_KEY } from '../store'
import { getConfig, saveBoard } from '../api'
import { colors } from '../styles'
import HeaderTabs from './HeaderTabs'

import { default as allSlots } from '../data/slots.js'
import { MEASURE_BOX_ACTIVE } from './LayoutSingleBoard'

export const EXPORT_PDF_ACTIVE = false
const FontFaceObserver = require('fontfaceobserver')

export const SIDEBAR_WIDTH = 300
export const HEADER_TAB_HEIGHT = 64
export const TARGET_CANVAS_WIDTH = 1100
export const TARGET_CANVAS_HEIGHT = 850

const DEFAULT_POSITION = () => {
  const el = document.querySelector('#canvas')
  const rect = el.getBoundingClientRect()
  console.log('rect', rect)
  return {
    x: Math.min(100, (Math.random() * rect.width) - 150),
    y: Math.min(100, (Math.random() * rect.height) - 150),
    // width: 150,
    // height: 150
  }
}

const DESIGN_ID_CACHE_KEY = 'current_design_ids'
const cacheDesignId = (id) => {
  const itemsStr = localStorage.getItem(DESIGN_ID_CACHE_KEY)
  let ids = []
  if (itemsStr) {
    ids = JSON.parse(itemsStr)
  }

  if (!ids.includes(id) && id !== 'NEW') {
    ids.push(id)
  }

  const removeIds = ids.slice(0, -10) // keep just the most recent ones
  // console.log('removing....', removeIds, ids)
  removeIds.forEach(oldId => {
    localStorage.removeItem(`design__${oldId}`)
    ids.shift()
  })

  localStorage.setItem(DESIGN_ID_CACHE_KEY, JSON.stringify(ids))
}

const useDimensions = ({ sidebarWidth = SIDEBAR_WIDTH, headerHeight = HEADER_TAB_HEIGHT } = {}) => {
  const [ dimensions, setDimensions ] = useState({ width: TARGET_CANVAS_WIDTH, height: TARGET_CANVAS_HEIGHT })
  useEffect(() => {
    const pageRatio = 0.77272727
    const containerPadding = 20

    function updateElementDimensions () {
      const wpAdminBar = document.querySelector('#wpadminbar')
      const adjustHeight = wpAdminBar ? wpAdminBar.clientHeight : 0
      const boardWidth = window.innerWidth - sidebarWidth
      const boardHeight = window.innerHeight - headerHeight - adjustHeight

      const baseWidth = boardWidth - containerPadding
      const baseHeight = boardHeight - containerPadding
      let newWidth = Math.round(baseWidth)
      let newHeight = Math.round(baseWidth * pageRatio)
      if (newHeight > baseHeight) {
        newWidth = Math.round(baseHeight / pageRatio)
        newHeight = Math.round(baseHeight)
      }
      const scaleX = newWidth / TARGET_CANVAS_WIDTH
      const scaleY = newHeight / TARGET_CANVAS_HEIGHT
      // console.log('setting dimensions', { TARGET_CANVAS_WIDTH, TARGET_CANVAS_HEIGHT, baseWidth, baseHeight, newWidth, newHeight, scaleX, scaleY })
      setDimensions({ width: newWidth, height: newHeight, scaleX, scaleY })
    }

    updateElementDimensions()

    const _debounced = debounce(updateElementDimensions, 100)
    window.addEventListener('resize', _debounced)

    return () => {
      window.removeEventListener('resize', _debounced)
    }
  }, [ sidebarWidth, headerHeight ])
  return dimensions
}

type Props = {
  initialItems: Array<DesignBoardItem>,
  saveItems: Function,
  currentDesignId: number,
  sidebar: boolean,
  headerTabs: boolean,
}

const SLOTS_ACTIVE = true

const addSlotId = (arr, indexPrefix) => {
  return arr.map((el, index) => ({ id: `slot-${indexPrefix}-${index}`, ...el }))
}

function Editor (props: Props) {
  const { dispatch, state, getCurrentBoard } = useContext(DesignContext)
  const { currentDesign, sidebar, headerTabs, printMode } = props
  const [ currentDimensions, setCurrentDimensions ] = useState({})
  const [ activeSlotId, setActiveSlotId ] = useState(null)
  const [ landingSpot, setLandingSpot ] = useState(null)
  const [ slots, saveSlots ] = useState([])
  const [ lastSlotFilled, saveFilledSlot ] = useState(null)
  const isCollection = window.Empire.DesignBoard.Settings[ 'isCollection' ] || sessionStorage.getItem('currentIsCollection') == 'true'

  let currentDesignId = props.currentDesignId
  // console.log('props.currentDesignId', currentDesignId)
  if (!currentDesignId && props.APP_ENV !== 'WP') {
    // console.log('pulling design id from sessionStorage')
    currentDesignId = sessionStorage.getItem(SAVED_DESIGN_ID_KEY)
  }

  // console.log('Editor', currentDesignId, props, state, currentDimensions)
  const board = getCurrentBoard()
  const { items } = board
  // let slots = allSlots[state.currentBoardIndex]

  const [ fontLoaded, setFontLoaded ] = useState(false)

  useEffect(() => {
    const mainFont = new FontFaceObserver('HelveticaNeue_Lt')

    mainFont.load().then(function () {
      // console.log('HelveticaNeue_Lt has loaded.');
      setFontLoaded(true)
    })
  }, [ fontLoaded ])

  useEffect(() => {
    if (!SLOTS_ACTIVE) { return }
    const curSlots = addSlotId(get(allSlots, state.currentBoardIndex, []), state.currentBoardIndex)
    // console.log('saving current slots', curSlots)
    saveSlots(curSlots)
  }, [ state.currentBoardIndex ])

  const { data: designConfig } = useQuery('config', getConfig)
  // console.log('editor data', designConfig)

  const sidebarWidth = sidebar ? SIDEBAR_WIDTH : 0
  const headerHeight = headerTabs ? HEADER_TAB_HEIGHT : 0
  const dimensions = useDimensions({ sidebarWidth, headerHeight })
  useEffect(() => {
    // console.log('updating dimensions in state')
    setCurrentDimensions(dimensions)
  }, [ setCurrentDimensions, dimensions ])

  const loadLocalData = useCallback(() => {
    console.log('trying to load local data', currentDesignId)
    const stored = localStorage.getItem(getStorageKey(currentDesignId))
    // console.log('stored?', stored)
    if (stored) {
      dispatch({ type: actions.LOAD_STATE, state: JSON.parse(stored), currentDesign })
    } else if (currentDesign) {
      dispatch({ type: actions.LOAD_STATE, state: currentDesign })
    }
  }, [ dispatch, currentDesignId, currentDesign ])

  useEffect(() => {
    console.log('checking for incoming design_id...', currentDesignId)
    if (currentDesignId) {
      console.log('got a design_id', currentDesignId)
      sessionStorage.setItem(SAVED_DESIGN_ID_KEY, currentDesignId)
      dispatch({ type: actions.UPDATE_DESIGN, key: 'id', value: currentDesignId })
      cacheDesignId(currentDesignId)
    }
  }, [ currentDesignId, dispatch ])

  useEffect(() => {
    loadLocalData()
  }, [ loadLocalData ])

  useEffect(() => {
    if (designConfig) {
      dispatch({ type: actions.LOAD_CONFIG, config: designConfig })
    }
  }, [ dispatch, designConfig ])

  const handleMouseUp = useCallback((evt) => {
    if (evt.target.tagName === 'CANVAS' && !MEASURE_BOX_ACTIVE) {
      console.log('saving landing spot', evt, evt.offsetX, evt.offsetY, currentDimensions)
      setLandingSpot({ x: evt.offsetX / currentDimensions.scaleX, y: evt.offsetY / currentDimensions.scaleY })
    }
  }, [ currentDimensions ])

  useEffect(() => {
    // console.log('setting up listener')
    document.addEventListener('dblclick', handleMouseUp)
    document.addEventListener('dbltap', handleMouseUp)
    // window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('dblclick', handleMouseUp)
      document.removeEventListener('dbltap', handleMouseUp)
      // window.removeEventListener('keydown', handleKeyDown)
    }
  }, [ handleMouseUp, currentDimensions ])

  const mutation = useMutation(saveBoard, {
    onSuccess: async (data) => {
      console.log('result', data)
      if (data.design) {
        await sessionStorage.setItem(SAVED_DESIGN_ID_KEY, data.design.id)
        await localStorage.setItem(UNSAVED_DESIGN_KEY, JSON.stringify(EMPTY_STATE))
        await dispatch({ type: actions.UPDATE_DESIGN, key: 'id', value: parseInt(data.design.id) })

        if (currentDesignId != data.design.id) {
          if (window.Empire.DesignBoard.Settings[ 'APP_ENV' ] === 'WP') {
            console.log('redirecting to saved post...', data.design.id)
            window.location = window.Empire.DesignBoard.Settings[ 'adminUrl' ] + `post.php?post=${data.design.id}&action=edit`
          }
        }
      }
    }
  })

  const saveBoardToServer = async () => {
    const { config: _, ...design } = state
    console.log('saving board to server ...', currentDesignId, design.id, design)
    const designId = design.id ? design.id : (currentDesignId !== 'NEW' ? currentDesignId : null)
    await mutation.mutate({ ...design, id: designId, isCollection })
  }

  const cloneBoard = async () => {
    await sessionStorage.setItem(SAVED_DESIGN_ID_KEY, 'NEW')
    await dispatch({ type: actions.CLONE_BOARD })
    if (window.Empire.DesignBoard.Settings[ 'APP_ENV' ] === 'WP') {
      console.log('redirecting to new post...')
      const url = window.Empire.DesignBoard.Settings[ 'adminUrl' ] + 'post-new.php?post_type=design-board'
      window.location = url
    }
  }

  const exportBoardToPdf = async () => {
    let cName = state.community?.title
    let lotName = state.lot?.label
    if (!cName) { cName = 'Empire Communities'}
    if (!lotName) { lotName = 'Lot X' }
    console.log('cName', cName, lotName)

    let docName = `${cName} - ${lotName}`
    const pdfDoc = new JsPDF({
      orientation: 'landscape',
      unit: 'in',
      format: 'letter'
    })

    const designId = sessionStorage.getItem(SAVED_DESIGN_ID_KEY)
    for (let b = 0; b < 3; b++) {
      const boardKey = `board_image_${designId}_${b}`
      const dataUrl = sessionStorage.getItem(boardKey)
      // console.log('adding board?', boardKey, dataUrl)
      if (!dataUrl) { continue }
      if (b > 0) {
        console.log('adding page')
        pdfDoc.addPage()
      }
      pdfDoc.addImage(dataUrl, 'JPEG', 0, 0, 11, 8.5)
    }

    pdfDoc.save(docName)

    // const el = document.querySelector('.x-print')
    // console.log('exportBoardToPdf', el)
    // el.dataset.printMode = true
    // pdfDoc.html(el, {
    //   callback: function (doc, args) {
    //     console.log('saving doc', args)
    //     doc.save(docName)
    //     el.dataset.printMode = false
    //   },
    //   x: 10,
    //   y: 10
    // })
  }

  function handleKeyDown (evt) {
    console.log('Editor.handleKeyDown', evt.key, evt.keyCode, evt)

    evt.stopImmediatePropagation();
    if (evt.key === 'Z' && (evt.ctrlKey || evt.metaKey) && evt.shiftKey) {
      // handle redo action
    } else if (evt.key === 'Z' && (evt.ctrlKey || evt.metaKey)) {
      // handle undo action
    }

    // if (evt.keyCode === 27) {
    //   deselect()
    // } else if (evt.keyCode === 46 /* DEL */ || evt.keyCode === 8 /* BACKSPACE */) {
    //   dispatch({ type: actions.REMOVE_ITEM, item: activeItem })
    //   setActiveItemId(null)
    // }
  }

  const handleChangeBoard = (boardIndex) => {
    // console.log('changing board', boardIndex)
    if (boardIndex >= 0) {
      setLandingSpot(null)
      dispatch({ type: actions.CHANGE_BOARD, index: boardIndex })
    }
  }

  const constrainPlacement = (attrs) => {
    let w = Math.ceil(attrs?.width)
    if (!w) { w = 50 }
    let rightEdge = (currentDimensions.width / currentDimensions.scaleX) - w
    attrs.x = Math.max(0, Math.min(attrs.x, rightEdge))

    let h = Math.ceil(attrs?.height)
    if (!h) { h = 50 }
    let bottomEdge = (currentDimensions.height / currentDimensions.scaleY) - h
    attrs.y = Math.max(0, Math.min(attrs.y, bottomEdge))

    return attrs
  }

  const handleUndoAddItem = (event) => {
    // console.log('undo?', event, lastSlotFilled)
    if (lastSlotFilled) {
      dispatch({ type: actions.CLEAR_SLOT, slot: lastSlotFilled })
      saveFilledSlot(null)
    }
  }

  useKeyboardShortcut(['Meta', 'z'], handleUndoAddItem, { overrideSystem: true })
  useKeyboardShortcut(['Control', 'z'], handleUndoAddItem, { overrideSystem: true })

  const handleAddItem = (event, itemToAdd, sampleGroup) => {
    console.log('adding item', { itemToAdd, board, sampleGroup, activeSlotId })

    const slot = activeSlotId ? slots.find(s => s.id === activeSlotId) : null
    const useLandingSpot = !activeSlotId && landingSpot?.x

    let imagePos = DEFAULT_POSITION()
    console.log('slot', { slot, imagePos, useLandingSpot })
    let textPos = { ...imagePos, x: imagePos.y - 12 }
    if (slot) {
      console.log('getting position from slot', slot)
      if (slot?.image) {
        imagePos = { ...slot.image }
      }
      if (slot?.text) {
        textPos = { ...slot.text }
      }
    }

    const { attributes: itemAttributes, ...item } = itemToAdd

    let { width, height } = itemAttributes
    // let textWidth = width
    let scale = 1
    if (activeSlotId && slot.image) {
      // console.log('placing on spot')
      scale = (slot.image.width / width)
      // console.log('scale', scale)
      width = slot.image.width
      height = height * scale
    } else if (sampleGroup?.initial_width > 0) {
      scale = (sampleGroup.initial_width / width) //* currentDimensions.scaleX
      width = width * scale
      height = height * scale
    } else if (sampleGroup?.initial_height > 0) {
      scale = (sampleGroup.initial_height / height) //* currentDimensions.scaleY
      width = width * scale
      height = height * scale
    } else {
      console.warn('no adjustment criteria available')
    }

    let { x, y } = imagePos
    if (useLandingSpot) {
      // console.log('placing on landing spot')
      x = landingSpot.x - (width * 0.5)
      y = landingSpot.y - (height * 0.5)
      setLandingSpot(null)
    }

    const finalImageAttrs = constrainPlacement({
      ...imagePos,
      ...itemAttributes,
      width,
      height,
      x,
      y,
    })
    const items = []
    console.log('checking if item has imageUrl', Boolean(item.imageUrl), useLandingSpot, item)
    if (item.imageUrl) {
      const image = {
        id: uuidv4(),
        image: item,
        attributes: finalImageAttrs,
        slotId: activeSlotId
      }
      items.push(image)
    }

    console.log('checking if item has label', Boolean(item.label && slot?.text), { itemLabel: item.label , slotText: slot?.text, useLandingSpot, item})
    // if ((item.label && slot?.text) || useLandingSpot) {
    if (item.label) {
      const adjustTextPos = (useLandingSpot) ? {
        x: finalImageAttrs.x,
        y: finalImageAttrs.y + finalImageAttrs.height
      } : {}
      const label = {
        id: uuidv4(),
        text: { label: item.label },
        attributes: constrainPlacement({
          ...textPos,
          ...adjustTextPos,
          height: 'auto',
        }),
        slotId: activeSlotId
      }
      items.push(label)
    }
    dispatch({ type: actions.ADD_ITEMS, board, items: items })
    setActiveSlotId(null)

    saveFilledSlot(slot)
  }

  const designBoardAttrs = {
    items,
    slots,
    dimensions,
    editable: sidebar,
    activeSlotId,
    setActiveSlotId,
    landingSpot,
    setLandingSpot,
    fontLoaded,
    printMode,
    isCollection,
  }
  const sidebarAttrs = {
    ...EXPORT_PDF_ACTIVE ? { onExportPDF: exportBoardToPdf } : {}
  }

  if (!fontLoaded) { return null }
  return (
    <Page>
      {sidebar && (
        <aside className="no-print" style={{ width: sidebarWidth }}>
          <Sidebar designId={currentDesignId} onAddItem={handleAddItem} onSave={saveBoardToServer} onClone={cloneBoard}
                   {...sidebarAttrs} isCollection={isCollection} onChangeBoard={handleChangeBoard} />
        </aside>
      )}

      <main>
        {headerTabs ? (
          <RightPanelStack>
            <HeaderTabs onChangeBoard={handleChangeBoard} />
            <DesignBoard {...designBoardAttrs} />
          </RightPanelStack>
        ) : (
          <DesignBoard {...designBoardAttrs} />
        )}
      </main>
    </Page>
  )
}

Editor.defaultProps = {
  printMode: false,
  sidebar: process.env.NODE_ENV !== 'production',
  headerTabs: process.env.NODE_ENV === 'production',
}

// Editor.whyDidYouRender = true

export default Editor

const Page = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;

  > aside {
    background: ${colors.lightGray};
    border-right: 2px solid ${colors.midGray};
    // width: 25%;
    height: 100vh;
    flex: 0 0 ${SIDEBAR_WIDTH}px;
    // width: 300px;
    overflow: scroll;
  }

  main {
    flex: 1 0 75%;
    display: flex;
    flex-direction: column;
  }

  @media print {
    aside {
      display: none;
    }
  }
`

const RightPanelStack = styled.div`


`